/**
 * 目的: 会員写真の「焦点位置」を CSS の object-position へ変換する
 *
 * 背景:
 * - 同じ1枚の写真を、一覧では 3:2、詳細と会員様の声では円形に切り取って表示する
 * - 縦長写真は 3:2 で縦がはみ出し、円では横がはみ出す。はみ出す方向が違う枠でも
 *   object-position は「指定した点が見えるように寄せる」ため、1つの値で両方に効く
 *
 * 方針:
 * - APIから壊れた値（範囲外・数値でない）が来ても描画が崩れないよう、常にフォールバックを持つ
 */

export type ImageFocal = { x: number; y: number };

/**
 * 目的: 焦点位置の1軸の値を 0-100 の整数に丸める
 * 引数: value - 生の数値
 * 戻り値: 0-100 の整数
 */
export function clampFocalValue(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 目的: APIの焦点位置を CSS の object-position 値へ変換する
 * 引数:
 * - focal: APIから受け取った焦点位置（未設定なら null）
 * - fallback: 未設定時に使う既存の表示位置（例: 'center top'）
 * 戻り値: object-position に渡す文字列
 */
export function focalToObjectPosition(
  focal: ImageFocal | null | undefined,
  fallback: string,
): string {
  if (!focal || !isFiniteNumber(focal.x) || !isFiniteNumber(focal.y)) {
    return fallback;
  }
  return `${clampFocalValue(focal.x)}% ${clampFocalValue(focal.y)}%`;
}

/**
 * 目的: 画像上のクリック/ドラッグ座標を焦点位置（パーセント）へ変換する
 * 引数:
 * - rect: 画像要素の getBoundingClientRect() の値
 * - clientX, clientY: ポインタイベントの座標
 * 戻り値: 0-100 に収めた ImageFocal
 * 注意: 画像が未読み込みなどで幅・高さが 0 のときは中央（50/50）を返す
 */
export function pointToFocal(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): ImageFocal {
  if (rect.width <= 0 || rect.height <= 0) {
    return { x: 50, y: 50 };
  }
  return {
    x: clampFocalValue(((clientX - rect.left) / rect.width) * 100),
    y: clampFocalValue(((clientY - rect.top) / rect.height) * 100),
  };
}

/** 元画像のうち、ある枠に実際に残る範囲（すべて元画像に対する％） */
export type FocalCropRegion = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/**
 * 目的: object-cover である縦横比の枠に収めたとき、元画像のどの範囲が残るかを求める
 * 背景:
 *   ピッカーの編集画面に「一覧カードではここが残る」「丸アイコンではここが残る」を
 *   重ねて描くために使う。プレビューを見比べなくても切れる範囲が分かるようにする。
 * 引数:
 * - sourceAspect: 元画像の縦横比（幅 / 高さ）
 * - targetAspect: 表示枠の縦横比（3:2 なら 1.5、丸アイコンなら 1）
 * - focal: 焦点位置（0-100）
 * 戻り値: 元画像に対する％の矩形
 * 注意: 縦横比が不明・不正なとき（画像未読み込みなど）は全体を返す
 */
export function focalCropRegion(
  sourceAspect: number,
  targetAspect: number,
  focal: ImageFocal,
): FocalCropRegion {
  const full = { left: 0, top: 0, width: 100, height: 100 };
  if (
    !isFiniteNumber(sourceAspect) ||
    !isFiniteNumber(targetAspect) ||
    sourceAspect <= 0 ||
    targetAspect <= 0 ||
    !isFiniteNumber(focal?.x) ||
    !isFiniteNumber(focal?.y)
  ) {
    return full;
  }

  // object-cover は短い方の辺を枠に合わせるので、長い方の辺だけがはみ出す。
  // はみ出さない辺は 1（＝100%）、はみ出す辺は「枠に入る割合」になる
  const width = Math.min(1, targetAspect / sourceAspect);
  const height = Math.min(1, sourceAspect / targetAspect);

  // object-position の％は「画像の n% の点を枠の n% の点に合わせる」意味なので、
  // 残る範囲の左上は「はみ出した分 × 焦点の割合」だけずれる
  const left = (1 - width) * (clampFocalValue(focal.x) / 100);
  const top = (1 - height) * (clampFocalValue(focal.y) / 100);

  return {
    left: left * 100,
    top: top * 100,
    width: width * 100,
    height: height * 100,
  };
}
